<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { debounce } from '@chatwoot/utils';
import Select from 'dashboard/components-next/select/Select.vue';
import Input from 'dashboard/components-next/input/Input.vue';

const props = defineProps({
  inboxes: {
    type: Array,
    default: () => [],
  },
});

// Null means "no filter" — kept as the public contract so the page can pass
// these straight through to ProviderLogsAPI.get({ status, inbox_id }).
const status = defineModel('status', { type: String, default: null });
const inboxId = defineModel('inboxId', { type: Number, default: null });
const phone = defineModel('phone', { type: String, default: null });

// O v-model do Input atualizaria `phone` (e disparariam o watch/fetch do
// pai) a cada tecla; o debounce evita uma requisicao por caractere digitado.
const phoneQuery = ref(phone.value || '');
const debouncedUpdatePhone = debounce(() => {
  phone.value = phoneQuery.value || null;
}, 500);

const { t } = useI18n();

// Message#status enum (app/models/message.rb): sent, delivered, read, failed.
const STATUS_VALUES = ['sent', 'delivered', 'read', 'failed'];

const statusOptions = computed(() => [
  { value: '', label: t('MESSAGE_LOGS_PAGE.FILTERS.ALL_STATUSES') },
  ...STATUS_VALUES.map(value => ({
    value,
    label: t(`MESSAGE_LOGS_PAGE.STATUS.${value.toUpperCase()}`),
  })),
]);

const inboxOptions = computed(() => [
  { value: '', label: t('MESSAGE_LOGS_PAGE.FILTERS.ALL_INBOXES') },
  ...props.inboxes.map(inbox => ({
    value: String(inbox.id),
    label: inbox.name,
  })),
]);

// Select.vue's native <select> only ever emits strings, so the null <->
// string bridging lives here rather than leaking into the parent page.
const statusValue = computed({
  get: () => status.value || '',
  set: value => {
    status.value = value || null;
  },
});

const inboxValue = computed({
  get: () => (inboxId.value ? String(inboxId.value) : ''),
  set: value => {
    inboxId.value = value ? Number(value) : null;
  },
});
</script>

<template>
  <div class="flex flex-wrap items-center gap-3">
    <div class="flex flex-col gap-1">
      <span class="text-label-small text-n-slate-11">
        {{ t('MESSAGE_LOGS_PAGE.FILTERS.STATUS') }}
      </span>
      <Select v-model="statusValue" :options="statusOptions" />
    </div>
    <div class="flex flex-col gap-1">
      <span class="text-label-small text-n-slate-11">
        {{ t('MESSAGE_LOGS_PAGE.FILTERS.INBOX') }}
      </span>
      <Select v-model="inboxValue" :options="inboxOptions" />
    </div>
    <div class="flex flex-col gap-1">
      <span class="text-label-small text-n-slate-11">
        {{ t('MESSAGE_LOGS_PAGE.FILTERS.PHONE') }}
      </span>
      <Input
        v-model="phoneQuery"
        type="search"
        :placeholder="t('MESSAGE_LOGS_PAGE.FILTERS.PHONE_PLACEHOLDER')"
        class="w-48"
        @input="debouncedUpdatePhone"
      />
    </div>
  </div>
</template>
